import type { Direction, SoundName, MovementStyle, Shape, MixedModeConfig } from '../types';

/**
 * MixedModeEngine - Manages randomization for Mixed Mode
 *
 * Features:
 * - Rapid color changes (every 1.5 seconds)
 * - Rapid direction changes (every 2 seconds)
 * - Rapid movement style changes (every 2.5 seconds)
 * - Rapid background changes (every 3 seconds)
 * - Rapid shape changes (every 3.5 seconds)
 * - Random sound selection from all available sounds (on each beep)
 */
export class MixedModeEngine {
  private colorInterval: number | null = null;
  private directionInterval: number | null = null;
  private movementStyleInterval: number | null = null;
  private backgroundInterval: number | null = null;
  private shapeInterval: number | null = null;
  private isActive: boolean = false;

  // Callbacks
  private onColorChange?: (color: string) => void;
  private onDirectionChange?: (direction: Direction) => void;
  private onMovementStyleChange?: (style: MovementStyle) => void;
  private onBackgroundChange?: (background: string) => void;
  private onShapeChange?: (shape: Shape) => void;

  // Available pools
  private availableColors: string[] = [];
  private availableDirections: Direction[] = [];
  private availableMovementStyles: MovementStyle[] = [];
  private availableBackgrounds: string[] = [];
  private availableShapes: Shape[] = [];
  private availableSounds: SoundName[] = [];

  // Timing configuration (in milliseconds) - RAPID FIRE for maximum chaos
  private colorChangeInterval: number = 1200; // 1.2 seconds
  private directionChangeInterval: number = 1500; // 1.5 seconds
  private movementStyleChangeInterval: number = 1800; // 1.8 seconds
  private backgroundChangeInterval: number = 2200; // 2.2 seconds
  private shapeChangeInterval: number = 2500; // 2.5 seconds

  constructor() {}

  /**
   * Start mixed mode randomization
   */
  start(config: {
    availableColors: string[];
    availableDirections: Direction[];
    availableMovementStyles: MovementStyle[];
    availableBackgrounds: string[];
    availableShapes: Shape[];
    availableSounds: SoundName[];
    randomizationConfig: MixedModeConfig;
    onColorChange: (color: string) => void;
    onDirectionChange: (direction: Direction) => void;
    onMovementStyleChange: (style: MovementStyle) => void;
    onBackgroundChange: (background: string) => void;
    onShapeChange: (shape: Shape) => void;
  }): void {
    // Stop any existing intervals first
    if (this.isActive) {
      this.stop();
    }

    this.availableColors = config.availableColors;
    this.availableDirections = config.availableDirections;
    this.availableMovementStyles = config.availableMovementStyles;
    this.availableBackgrounds = config.availableBackgrounds;
    this.availableShapes = config.availableShapes;
    this.availableSounds = config.availableSounds;
    this.onColorChange = config.onColorChange;
    this.onDirectionChange = config.onDirectionChange;
    this.onMovementStyleChange = config.onMovementStyleChange;
    this.onBackgroundChange = config.onBackgroundChange;
    this.onShapeChange = config.onShapeChange;
    this.isActive = true;

    // Start color randomization (only if enabled in config)
    if (config.randomizationConfig.randomizeColor && this.availableColors.length > 0) {
      this.colorInterval = window.setInterval(() => {
        if (this.availableColors.length > 0) {
          const randomColor = this.availableColors[
            Math.floor(Math.random() * this.availableColors.length)
          ];
          this.onColorChange?.(randomColor);
        }
      }, this.colorChangeInterval);
    }

    // Start direction randomization (only if enabled in config)
    if (config.randomizationConfig.randomizeDirection && this.availableDirections.length > 0) {
      this.directionInterval = window.setInterval(() => {
        if (this.availableDirections.length > 0) {
          const randomDirection = this.availableDirections[
            Math.floor(Math.random() * this.availableDirections.length)
          ];
          this.onDirectionChange?.(randomDirection);
        }
      }, this.directionChangeInterval);
    }

    // Start movement style randomization (only if enabled in config)
    if (config.randomizationConfig.randomizeMovementStyle && this.availableMovementStyles.length > 0) {
      this.movementStyleInterval = window.setInterval(() => {
        if (this.availableMovementStyles.length > 0) {
          const randomStyle = this.availableMovementStyles[
            Math.floor(Math.random() * this.availableMovementStyles.length)
          ];
          this.onMovementStyleChange?.(randomStyle);
        }
      }, this.movementStyleChangeInterval);
    }

    // Start background randomization (only if enabled in config)
    if (config.randomizationConfig.randomizeBackground && this.availableBackgrounds.length > 0) {
      this.backgroundInterval = window.setInterval(() => {
        if (this.availableBackgrounds.length > 0) {
          const randomBackground = this.availableBackgrounds[
            Math.floor(Math.random() * this.availableBackgrounds.length)
          ];
          this.onBackgroundChange?.(randomBackground);
        }
      }, this.backgroundChangeInterval);
    }

    // Start shape randomization (only if enabled in config)
    if (config.randomizationConfig.randomizeShape && this.availableShapes.length > 0) {
      this.shapeInterval = window.setInterval(() => {
        if (this.availableShapes.length > 0) {
          const randomShape = this.availableShapes[
            Math.floor(Math.random() * this.availableShapes.length)
          ];
          this.onShapeChange?.(randomShape);
        }
      }, this.shapeChangeInterval);
    }

    console.log('[MixedModeEngine] Started - RAPID FIRE randomization (color:1.2s, dir:1.5s, style:1.8s, bg:2.2s, shape:2.5s)');
  }

  /**
   * Stop all randomization
   */
  stop(): void {
    if (this.colorInterval !== null) {
      clearInterval(this.colorInterval);
      this.colorInterval = null;
    }
    if (this.directionInterval !== null) {
      clearInterval(this.directionInterval);
      this.directionInterval = null;
    }
    if (this.movementStyleInterval !== null) {
      clearInterval(this.movementStyleInterval);
      this.movementStyleInterval = null;
    }
    if (this.backgroundInterval !== null) {
      clearInterval(this.backgroundInterval);
      this.backgroundInterval = null;
    }
    if (this.shapeInterval !== null) {
      clearInterval(this.shapeInterval);
      this.shapeInterval = null;
    }
    this.isActive = false;
    console.log('[MixedModeEngine] Stopped');
  }

  /**
   * Get random sound from available list
   * Called on each beep by audio engine
   */
  getRandomSound(): SoundName {
    if (this.availableSounds.length === 0) {
      return 'tap'; // Fallback
    }
    return this.availableSounds[Math.floor(Math.random() * this.availableSounds.length)];
  }

  /**
   * Check if currently active
   */
  getIsActive(): boolean {
    return this.isActive;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stop();
  }
}

// Singleton instance
let mixedModeEngineInstance: MixedModeEngine | null = null;

/**
 * Get singleton instance
 */
export function getMixedModeEngine(): MixedModeEngine {
  if (!mixedModeEngineInstance) {
    mixedModeEngineInstance = new MixedModeEngine();
  }
  return mixedModeEngineInstance;
}
